// app/(app)/payments/add.tsx
import { Button, Input } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { locationApi } from "@/services/locationApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { createPayment } from "@/store/slices/paymentSlice";
import { showAlert } from "@/utils/alert";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

export default function AddPaymentScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { customers } = useAppSelector((state) => state.customers);
  const { updating, error } = useAppSelector((state) => state.payments);

  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState<"cash" | "bank" | "damage">(
    "cash",
  );
  const [paymentDate, setPaymentDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [referenceNo, setReferenceNo] = useState("");
  const [errors, setErrors] = useState({
    customer: "",
    amount: "",
    paymentType: "",
  });
  const [customerSearch, setCustomerSearch] = useState("");

  // Load areas on mount
  useEffect(() => {
    loadAreas();
  }, []);

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Fetch customers when area changes
  useEffect(() => {
    if (selectedAreaId) {
      dispatch(
        fetchCustomers({
          page: 1,
          limit: 100,
          search: "",
          areaId: parseInt(selectedAreaId),
        }),
      );
      setSelectedCustomerId("");
      setSelectedCustomer(null);
    }
  }, [selectedAreaId]);

  // Debounced customer search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (selectedAreaId) {
        dispatch(
          fetchCustomers({
            page: 1,
            limit: 20,
            search: customerSearch,
            areaId: parseInt(selectedAreaId),
          }),
        );
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  // Reset form (keep area and customer optionally)
  const resetForm = (keepAreaAndCustomer = false) => {
    setAmount("");
    setPaymentType("cash");
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setReferenceNo("");
    setErrors({ customer: "", amount: "", paymentType: "" });
    if (!keepAreaAndCustomer) {
      setSelectedAreaId("");
      setSelectedCustomerId("");
      setSelectedCustomer(null);
    }
  };

  // On focus, reset fully (fresh start) – but only if there's no area selected (i.e., initial load)
  useFocusEffect(
    useCallback(() => {
      // Only reset if no area is selected (fresh start)
      if (!selectedAreaId) {
        resetForm(false);
      }
      // If area is already selected (user is in middle of adding), keep it
    }, [selectedAreaId]),
  );

  const validate = () => {
    let valid = true;
    const newErrors = { customer: "", amount: "", paymentType: "" };
    if (!selectedCustomerId) {
      newErrors.customer = "Select a customer";
      valid = false;
    }
    const amt = parseFloat(amount);
    if (!amount) {
      newErrors.amount = "Amount is required";
      valid = false;
    } else if (isNaN(amt) || amt <= 0) {
      newErrors.amount = "Amount must be greater than 0";
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      customerId: parseInt(selectedCustomerId),
      amount: parseFloat(amount),
      paymentType,
      paymentDate,
      referenceNo: referenceNo.trim() || undefined,
    };
    const result = await dispatch(createPayment(payload));
    if (createPayment.fulfilled.match(result)) {
      // ✅ Stay on screen – reset fields but keep area & customer
      showAlert("Success", "Payment recorded successfully", [
        {
          text: "OK",
          onPress: () => {
            resetForm(true); // keep area and customer
          },
        },
      ]);
    }
  };

  useEffect(() => {
    if (error) showAlert("Error", error);
  }, [error]);

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.lg,
    },
    inputContainer: { marginBottom: spacing.md },
    balanceContainer: {
      backgroundColor: colors.border + "30",
      padding: spacing.md,
      borderRadius: 8,
      marginBottom: spacing.md,
    },
    balanceText: { fontSize: 14, color: colors.text },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Record Payment</Text>

          <SearchableSelect
            label="Area / Route"
            placeholder="Select area"
            options={areas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId}
            onValueChange={setSelectedAreaId}
            loading={loadingAreas}
            containerStyle={styles.inputContainer}
            clearable
          />

          <SearchableSelect
            label="Customer"
            placeholder="Search by name or phone"
            options={customers.map((c) => ({
              label: `${c.fullName} (${c.phone})-${c.address}`,
              value: c.id.toString(),
            }))}
            value={selectedCustomerId}
            onValueChange={(val, label) => {
              setSelectedCustomerId(val);
              const cust = customers.find((c) => c.id.toString() === val);
              setSelectedCustomer(cust);
            }}
            onSearch={setCustomerSearch}
            error={errors.customer}
            containerStyle={styles.inputContainer}
            disabled={!selectedAreaId}
            clearable
          />

          {selectedCustomer && (
            <View style={styles.balanceContainer}>
              <Text style={styles.balanceText}>
                Balance: ₹{parseFloat(selectedCustomer.balance || 0).toFixed(2)}
              </Text>
            </View>
          )}

          <Input
            label="Amount (₹)"
            value={amount}
            onChangeText={setAmount}
            keyboardType="numeric"
            error={errors.amount}
            containerStyle={styles.inputContainer}
          />

          <SearchableSelect
            label="Payment Type"
            options={[
              { label: "Cash", value: "cash" },
              { label: "Bank", value: "bank" },
              { label: "Damage", value: "damage" },
            ]}
            value={paymentType}
            onValueChange={(val) => setPaymentType(val as any)}
            containerStyle={styles.inputContainer}
          />

          <DatePicker
            label="Payment Date"
            value={paymentDate}
            onChange={setPaymentDate}
            containerStyle={styles.inputContainer}
          />

          <Input
            label="Reference No (Optional)"
            placeholder="Cheque/UTR number"
            value={referenceNo}
            onChangeText={setReferenceNo}
            containerStyle={styles.inputContainer}
          />

          <Button
            title="Record Payment"
            onPress={handleSubmit}
            loading={updating}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
